import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, FlaskConical } from 'lucide-react';
import { api } from '../../api';

export const EditWorkshopModal = ({ workshop, orgTrainers = [], onClose, onSuccess }) => {
  const { t } = useTranslation();
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    delivery_date: workshop.delivery_date,
    location: workshop.location || '',
    expected_attendees: workshop.expected_attendees || '',
    notes: workshop.notes || '',
    owner_id: workshop.user_id || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [labRequest, setLabRequest] = useState(null);
  const [showLabCancelWarning, setShowLabCancelWarning] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);

  useEffect(() => {
    api.request(`/lab-requests/by-workshop/${workshop.id}`).then(req => {
      setLabRequest(req || null);
    }).catch(() => setLabRequest(null));
  }, [workshop.id]);

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dateChanged = formData.delivery_date !== workshop.delivery_date;
    if (labRequest && dateChanged) {
      const daysUntil = getDaysUntil(formData.delivery_date);
      if (daysUntil < 3) {
        setPendingFormData(formData);
        setShowLabCancelWarning(true);
        return;
      }
    }
    await doSave(formData, dateChanged);
  };

  const doSave = async (data, dateChanged) => {
    setSubmitting(true);
    try {
      await api.request(`/workshops/${workshop.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          delivery_date: data.delivery_date,
          location: data.location,
          expected_attendees: parseInt(data.expected_attendees),
          notes: data.notes,
          ...(orgTrainers.length > 0 && data.owner_id !== workshop.user_id ? { new_owner_id: data.owner_id } : {})
        })
      });
      if (labRequest && dateChanged) {
        const daysUntil = getDaysUntil(data.delivery_date);
        if (daysUntil >= 3) {
          try {
            await api.request(`/lab-requests/by-workshop/${workshop.id}/update-date`, {
              method: 'PUT',
              body: JSON.stringify({ new_date: data.delivery_date })
            });
          } catch (err) {
            console.error('Lab request date update failed:', err);
          }
        }
      }
      onSuccess();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCancelLab = async () => {
    setShowLabCancelWarning(false);
    setSubmitting(true);
    try {
      await api.request(`/lab-requests/by-workshop/${workshop.id}/cancel`, { method: 'PUT' });
    } catch (err) {
      console.error('Lab cancellation failed:', err);
    }
    await doSave(pendingFormData, true);
  };

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      await api.request(`/workshops/${workshop.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'cancelled' })
      });
      onSuccess();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const daysUntilNew = formData.delivery_date ? getDaysUntil(formData.delivery_date) : null;
  const dateChangedAndLab = labRequest && formData.delivery_date !== workshop.delivery_date;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="h-2 bg-levelup-dark rounded-t-2xl"></div>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-levelup-dark">{t('workshops.editWorkshop')}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={20} />
            </button>
          </div>

          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Course: <span className="font-medium text-levelup-dark">{workshop.course_title}</span></p>
            {labRequest && (
              <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                <FlaskConical size={12} />
                Active lab request (status: {labRequest.status})
              </p>
            )}
          </div>

          {showLabCancelWarning ? (
            <div className="text-center py-4">
              <AlertTriangle className="mx-auto text-orange-500 mb-3" size={48} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Workshop Starts in Less Than 3 Days</h3>
              <p className="text-gray-600 mb-3">
                Labs cannot be provisioned in this timeframe. Rescheduling to <strong>{pendingFormData?.delivery_date}</strong> will automatically <strong className="text-red-600">cancel your active lab request</strong>.
              </p>
              <p className="text-gray-500 text-sm mb-6">A cancellation notification will be sent to the lab provisioning team.</p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLabCancelWarning(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                >
                  Go Back
                </button>
                <button
                  onClick={handleConfirmCancelLab}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 disabled:opacity-50"
                  data-testid="confirm-cancel-lab-reschedule"
                >
                  {submitting ? 'Processing...' : 'Reschedule & Cancel Lab'}
                </button>
              </div>
            </div>
          ) : showCancelConfirm ? (
            <div className="text-center py-4">
              <AlertTriangle className="mx-auto text-red-500 mb-3" size={48} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('workshops.cancelWorkshopConfirm')}</h3>
              <p className="text-gray-600 mb-6">{t('workshops.cancelWorkshopWarning')}</p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                >
                  {t('common.back')}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 disabled:opacity-50"
                  data-testid="confirm-cancel-workshop"
                >
                  {submitting ? t('common.cancelling') : t('workshops.confirmCancel')}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshops.date')}</label>
                <input
                  type="date"
                  className="input-field"
                  value={formData.delivery_date}
                  onChange={(e) => setFormData({...formData, delivery_date: e.target.value})}
                  min={today}
                  required
                  data-testid="edit-date-input"
                />
                {dateChangedAndLab && daysUntilNew !== null && daysUntilNew >= 3 && (
                  <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                    <FlaskConical size={12} />
                    Your lab request will be automatically updated to the new date.
                  </p>
                )}
                {dateChangedAndLab && daysUntilNew !== null && daysUntilNew < 3 && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertTriangle size={12} />
                    Warning: Saving will cancel your active lab request (less than 3 days).
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshops.location')}</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="City, Country or Virtual"
                  required
                  data-testid="edit-location-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshops.expectedAttendees')}</label>
                <input
                  type="number"
                  className="input-field"
                  value={formData.expected_attendees}
                  onChange={(e) => setFormData({...formData, expected_attendees: e.target.value})}
                  min="1"
                  required
                  data-testid="edit-attendees-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshops.notes')}</label>
                <textarea
                  className="input-field"
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  data-testid="edit-notes-input"
                />
              </div>

              {orgTrainers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trainer (Owner)</label>
                  <select
                    className="input-field"
                    value={formData.owner_id}
                    onChange={(e) => setFormData({...formData, owner_id: e.target.value})}
                    data-testid="edit-owner-select"
                  >
                    {!orgTrainers.some(t => t.id === workshop.user_id) && (
                      <option key={workshop.user_id} value={workshop.user_id}>
                        {workshop.trainer_name || workshop.trainer_email || 'Current owner'} (current owner — no longer active)
                      </option>
                    )}
                    {orgTrainers.map(trainer => (
                      <option key={trainer.id} value={trainer.id}>
                        {trainer.full_name || trainer.email}{trainer.id === workshop.user_id ? ' (current owner)' : ''}
                      </option>
                    ))}
                  </select>
                  {formData.owner_id !== workshop.user_id && (
                    <p className="text-xs text-blue-600 mt-1">
                      Ownership will be transferred. The previous owner will be notified by email.
                    </p>
                  )}
                </div>
              )}

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCancelConfirm(true)}
                  className="flex-1 px-4 py-2.5 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 border border-red-200"
                  data-testid="cancel-workshop-btn"
                >
                  {t('workshops.cancelWorkshop')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-levelup-accent text-levelup-darker rounded-lg font-semibold hover:bg-levelup-accent-hover disabled:opacity-50"
                  data-testid="save-workshop-btn"
                >
                  {submitting ? t('common.saving') : t('common.saveChanges')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
